import { useEffect, useMemo, useState } from "react";
import api from "../../../utils/api";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { toServerDateTime } from "../calendarUtils";
import {
    CALL_TYPE_GOOGLE_MEET,
    CALL_TYPE_LANGUAGEON,
    CALL_TYPE_OFFLINE,
    CALL_TYPE_OPTIONS,
} from "../lessonCallTypes";

const WEEKDAYS = [
    { value: 0, label: "Sunday", short: "Sun" },
    { value: 1, label: "Monday", short: "Mon" },
    { value: 2, label: "Tuesday", short: "Tue" },
    { value: 3, label: "Wednesday", short: "Wed" },
    { value: 4, label: "Thursday", short: "Thu" },
    { value: 5, label: "Friday", short: "Fri" },
    { value: 6, label: "Saturday", short: "Sat" },
];

function formatLocalYmd(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function createEmptyDaySchedules() {
    const o = {};
    for (let i = 0; i <= 6; i++) {
        o[i] = { enabled: false, time_start: "10:00", time_end: "11:00" };
    }
    return o;
}

/**
 * @param {object} opts
 * @param {Record<number, { enabled: boolean, time_start: string, time_end: string }>} opts.daySchedules
 */
export function buildBatchLessonPayloads(opts) {
    const {
        periodStart,
        periodEnd,
        daySchedules,
        classroom_id,
        status,
        call_type,
        call_link,
        notes,
    } = opts;

    const lessons = [];
    const rangeStart = new Date(`${periodStart}T12:00:00`);
    const rangeEnd = new Date(`${periodEnd}T12:00:00`);
    if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime()) || rangeStart > rangeEnd) {
        return lessons;
    }

    for (let t = rangeStart.getTime(); t <= rangeEnd.getTime(); t += 86400000) {
        const d = new Date(t);
        const dow = d.getDay();
        const cfg = daySchedules[dow];
        if (!cfg?.enabled) continue;

        const ymd = formatLocalYmd(d);
        const [y, mo, day] = ymd.split("-").map(Number);
        const [sh, sm] = cfg.time_start.split(":").map(Number);
        const [eh, em] = cfg.time_end.split(":").map(Number);
        if ([y, mo, day, sh, sm, eh, em].some((n) => Number.isNaN(n))) continue;
        const startDt = new Date(y, mo - 1, day, sh, sm, 0);
        const endDt = new Date(y, mo - 1, day, eh, em, 0);
        if (endDt <= startDt) continue;

        const ct = call_type || CALL_TYPE_LANGUAGEON;
        lessons.push({
            classroom_id: Number(classroom_id),
            starts_at: toServerDateTime(startDt),
            ends_at: toServerDateTime(endDt),
            status: status || "planned",
            call_type: ct,
            call_link: ct === CALL_TYPE_GOOGLE_MEET ? (call_link || null) : null,
            notes: notes || null,
        });
    }
    return lessons;
}

const initialForm = {
    classroom_id: "",
    period_start: "",
    period_end: "",
    status: "planned",
    call_type: CALL_TYPE_LANGUAGEON,
    call_link: "",
    notes: "",
};

export default function CreateBatchLessonsModal({
    open,
    onClose,
    classrooms = [],
    defaultClassroomId = "",
    hiddenWeekdays = [],
}) {
    const [form, setForm] = useState(initialForm);
    const [daySchedules, setDaySchedules] = useState(createEmptyDaySchedules);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "call_type" && (value === CALL_TYPE_LANGUAGEON || value === CALL_TYPE_OFFLINE)) {
                next.call_link = "";
            }
            return next;
        });
    };

    useEffect(() => {
        if (!open) return;
        const today = new Date();
        const endDefault = new Date(today);
        endDefault.setMonth(endDefault.getMonth() + 2);
        setForm({
            ...initialForm,
            classroom_id: defaultClassroomId ? String(defaultClassroomId) : "",
            period_start: formatLocalYmd(today),
            period_end: formatLocalYmd(endDefault),
        });
        setDaySchedules(createEmptyDaySchedules());
        setErrors({});
        setSaving(false);
    }, [open, defaultClassroomId]);

    const handleAddDay = (d) => {
        setDaySchedules((prev) => ({
            ...prev,
            [d]: { ...prev[d], enabled: true },
        }));
    };

    const handleRemoveDayFromBatch = (d) => {
        setDaySchedules((prev) => ({
            ...prev,
            [d]: { ...prev[d], enabled: false },
        }));
    };

    const updateDayTimes = (d, field, value) => {
        setDaySchedules((prev) => ({
            ...prev,
            [d]: { ...prev[d], [field]: value },
        }));
    };

    const previewCount = useMemo(() => {
        if (!form.period_start || !form.period_end || !form.classroom_id) return 0;
        return buildBatchLessonPayloads({
            periodStart: form.period_start,
            periodEnd: form.period_end,
            daySchedules,
            classroom_id: form.classroom_id,
            status: form.status,
            call_type: form.call_type,
            call_link: form.call_link,
            notes: form.notes,
        }).length;
    }, [
        form.period_start,
        form.period_end,
        form.classroom_id,
        form.status,
        form.call_type,
        form.call_link,
        form.notes,
        daySchedules,
    ]);

    const enabledCount = useMemo(
        () =>
            WEEKDAYS.filter(
                (w) => !hiddenWeekdays.includes(w.value) && daySchedules[w.value].enabled
            ).length,
        [daySchedules, hiddenWeekdays]
    );

    const availableWeekdays = useMemo(
        () => WEEKDAYS.filter((w) => !hiddenWeekdays.includes(w.value)),
        [hiddenWeekdays]
    );

    const handleSubmit = async () => {
        setErrors({});

        if (!form.classroom_id) {
            setErrors({ classroom_id: ["Choose a classroom."] });
            return;
        }
        if (!form.period_start || !form.period_end) {
            setErrors({ period: ["Choose start and end of the period."] });
            return;
        }
        if (enabledCount === 0) {
            setErrors({ days: ["Add at least one weekday below."] });
            return;
        }
        if (form.call_type === CALL_TYPE_GOOGLE_MEET && !String(form.call_link || "").trim()) {
            setErrors({ call_link: ["Meeting link is required for Google Meet."] });
            return;
        }

        const lessons = buildBatchLessonPayloads({
            periodStart: form.period_start,
            periodEnd: form.period_end,
            daySchedules,
            classroom_id: form.classroom_id,
            status: form.status,
            call_type: form.call_type,
            call_link: form.call_link,
            notes: form.notes,
        });

        if (lessons.length === 0) {
            setErrors({
                period: [
                    "No lessons generated. Check each selected day has end time after start time, and the date range includes those weekdays.",
                ],
            });
            return;
        }

        if (lessons.length > 500) {
            setErrors({ period: ["Maximum 500 lessons per batch. Narrow the date range or fewer weekdays."] });
            return;
        }

        try {
            setSaving(true);
            await api.post("admin/classroom-lessons/batch", { lessons });
            onClose(true);
        } catch (e) {
            const errs = e.response?.data?.errors;
            setErrors(errs && typeof errs === "object" ? errs : { _form: [e.response?.data?.message || e.message] });
        } finally {
            setSaving(false);
        }
    };

    const formError = errors._form?.[0];
    const periodError = errors.period?.[0];
    const daysError = errors.days?.[0];

    const daysToAdd = availableWeekdays.filter((w) => !daySchedules[w.value].enabled);

    return (
        <Dialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle>Create batch lessons</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Typography variant="body2" color="text.secondary">
                        Pick a date range, then add weekdays. Each added day gets its own section with lesson start and
                        end time.
                    </Typography>

                    {formError ? (
                        <Typography color="error" variant="body2">
                            {formError}
                        </Typography>
                    ) : null}
                    {periodError ? (
                        <Typography color="error" variant="body2">
                            {periodError}
                        </Typography>
                    ) : null}
                    {daysError ? (
                        <Typography color="error" variant="body2">
                            {daysError}
                        </Typography>
                    ) : null}

                    <FormControl error={!!errors.classroom_id?.[0]}>
                        <FormLabel htmlFor="batch_classroom">Classroom</FormLabel>
                        <Select
                            id="batch_classroom"
                            name="classroom_id"
                            value={form.classroom_id}
                            onChange={handleChange}
                            error={!!errors.classroom_id?.[0]}
                        >
                            {classrooms.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                    {c.name || c.classroom_name || `Classroom #${c.id}`}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.classroom_id && (
                            <FormHelperText error>{errors.classroom_id[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControl sx={{ flex: 1 }} error={!!errors.period_start}>
                            <FormLabel htmlFor="batch_period_start">Period from</FormLabel>
                            <OutlinedInput
                                id="batch_period_start"
                                name="period_start"
                                type="date"
                                fullWidth
                                value={form.period_start}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }} error={!!errors.period_end}>
                            <FormLabel htmlFor="batch_period_end">Period to</FormLabel>
                            <OutlinedInput
                                id="batch_period_end"
                                name="period_end"
                                type="date"
                                fullWidth
                                value={form.period_end}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </FormControl>
                    </Stack>

                    <FormControl error={!!errors.status?.[0]}>
                        <FormLabel htmlFor="batch_status">Status</FormLabel>
                        <Select id="batch_status" name="status" value={form.status} onChange={handleChange}>
                            <MenuItem value="planned">Planned</MenuItem>
                            <MenuItem value="in_progress">In progress</MenuItem>
                            <MenuItem value="done">Done</MenuItem>
                            <MenuItem value="canceled">Canceled</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl error={!!errors.call_type?.[0]}>
                        <FormLabel htmlFor="batch_call_type">Call type</FormLabel>
                        <Select
                            id="batch_call_type"
                            name="call_type"
                            value={form.call_type}
                            onChange={handleChange}
                        >
                            {CALL_TYPE_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {form.call_type === CALL_TYPE_GOOGLE_MEET ? (
                        <FormControl error={!!errors.call_link?.[0]}>
                            <FormLabel htmlFor="batch_call_link">Meeting link</FormLabel>
                            <OutlinedInput
                                id="batch_call_link"
                                name="call_link"
                                fullWidth
                                value={form.call_link}
                                onChange={handleChange}
                                placeholder="https://meet.google.com/…"
                            />
                            <FormHelperText>Required for Google Meet.</FormHelperText>
                            {errors.call_link?.[0] ? (
                                <FormHelperText error>{errors.call_link[0]}</FormHelperText>
                            ) : null}
                        </FormControl>
                    ) : null}
                    {form.call_type === CALL_TYPE_OFFLINE ? (
                        <FormHelperText sx={{ mt: -1 }}>
                            A lesson reference link is generated for each created lesson.
                        </FormHelperText>
                    ) : null}

                    <FormControl error={!!errors.notes?.[0]}>
                        <FormLabel htmlFor="batch_notes">Notes</FormLabel>
                        <OutlinedInput
                            id="batch_notes"
                            name="notes"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.notes}
                            onChange={handleChange}
                        />
                    </FormControl>

                    <Divider sx={{ my: 1 }} />

                    <Typography variant="subtitle2">Weekday schedule</Typography>
                    <Box sx={{ mb: 2 }}>
                        {availableWeekdays.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                All weekdays are disabled in calendar settings.
                            </Typography>
                        ) : daysToAdd.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                All available weekdays are in this batch.
                            </Typography>
                        ) : (
                            <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                                {daysToAdd.map((w) => (
                                    <Button
                                        key={w.value}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleAddDay(w.value)}
                                    >
                                        {w.label}
                                    </Button>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    {enabledCount === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            No weekdays yet. Tap a day above to add it.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            {availableWeekdays.map((w) => {
                                if (!daySchedules[w.value].enabled) return null;
                                const cfg = daySchedules[w.value];
                                return (
                                    <Box
                                        key={w.value}
                                        sx={{
                                            p: 2,
                                            borderRadius: 1,
                                            border: 1,
                                            borderColor: "divider",
                                            bgcolor: "background.paper",
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ mb: 1.5 }}
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {w.label}
                                            </Typography>
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                                onClick={() => handleRemoveDayFromBatch(w.value)}
                                            >
                                                Remove
                                            </Button>
                                        </Stack>
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                            <FormControl sx={{ flex: 1 }} fullWidth>
                                                <FormLabel htmlFor={`batch_day_${w.value}_start`}>
                                                    Lesson start
                                                </FormLabel>
                                                <OutlinedInput
                                                    id={`batch_day_${w.value}_start`}
                                                    type="time"
                                                    fullWidth
                                                    value={cfg.time_start}
                                                    onChange={(e) =>
                                                        updateDayTimes(w.value, "time_start", e.target.value)
                                                    }
                                                />
                                            </FormControl>
                                            <FormControl sx={{ flex: 1 }} fullWidth>
                                                <FormLabel htmlFor={`batch_day_${w.value}_end`}>Lesson end</FormLabel>
                                                <OutlinedInput
                                                    id={`batch_day_${w.value}_end`}
                                                    type="time"
                                                    fullWidth
                                                    value={cfg.time_end}
                                                    onChange={(e) =>
                                                        updateDayTimes(w.value, "time_end", e.target.value)
                                                    }
                                                />
                                            </FormControl>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        Preview: <strong>{previewCount}</strong> lesson(s) will be created ({enabledCount} weekday
                        {enabledCount === 1 ? "" : "s"} in range).
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving || previewCount === 0}>
                    {saving ? <CircularProgress size={24} /> : `Create ${previewCount || ""} lessons`.trim()}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
